import React, { useState, useMemo } from 'react';
import './AccumulationCalculator.css';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar'];

const SCENARIOS = [
  {
    id: 'a',
    billing: 'Quarterly',
    window: '1 month',
    desc: 'Each month is priced on its own usage. The three results are collected and billed together in one invoice at quarter end.',
  },
  {
    id: 'b',
    billing: 'Quarterly',
    window: '3 months',
    desc: 'Usage from all three months is pooled first. The pricing model runs once on the combined total. One invoice at quarter end.',
  },
  {
    id: 'c',
    billing: 'Monthly',
    window: '3 months',
    desc: 'An invoice goes out every month, but each only charges the new usage at the cumulative rate — subtracting what was already billed.',
  },
];

const DEFAULT_USAGE  = [100, 200, 300];
const DEFAULT_TIERS  = [{ upTo: 200, rate: 5 }, { upTo: Infinity, rate: 3 }];

// ── Calculation helpers ───────────────────────────────────────────────────────

function calcCost(units, tiers) {
  let remaining = Math.max(0, units);
  let total = 0;
  let prev = 0;
  for (const { upTo, rate } of tiers) {
    const band = upTo === Infinity ? remaining : Math.min(remaining, upTo - prev);
    if (band <= 0) break;
    total += band * rate;
    remaining -= band;
    prev = upTo;
    if (remaining <= 0) break;
  }
  return total;
}

function tierLines(units, tiers) {
  const lines = [];
  let remaining = Math.max(0, units);
  let prev = 0;
  for (const { upTo, rate } of tiers) {
    const band = upTo === Infinity ? remaining : Math.min(remaining, upTo - prev);
    if (band <= 0) break;
    lines.push({ from: prev, to: upTo, band, rate, cost: band * rate });
    remaining -= band;
    prev = upTo;
    if (remaining <= 0) break;
  }
  return lines;
}

// ── Formatting ────────────────────────────────────────────────────────────────

function fmt(n) {
  if (n === 0) return '₹0';
  return '₹' + n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

// ── Tier breakdown row ────────────────────────────────────────────────────────

function TierBreakdown({ units, tiers }) {
  const lines = tierLines(units, tiers);
  if (!lines.length) return null;
  return (
    <div className="ac-breakdown">
      {lines.map((l, i) => (
        <div key={i} className="ac-breakdown-row">
          <span className="ac-breakdown-range">
            {l.to === Infinity ? `${l.from + 1}+ units` : `${l.from + 1}–${l.to} units`}
          </span>
          <span className="ac-breakdown-calc">
            {l.band} × {fmt(l.rate)} = <strong>{fmt(l.cost)}</strong>
          </span>
        </div>
      ))}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function AccumulationCalculator() {
  const [scenarioId, setScenarioId]   = useState('a');
  const [usage, setUsage]             = useState(DEFAULT_USAGE);
  const [tiers, setTiers]             = useState(DEFAULT_TIERS);
  const [expanded, setExpanded]       = useState({});

  const scenario = SCENARIOS.find(s => s.id === scenarioId);

  const result = useMemo(() => {
    if (scenarioId === 'a') {
      const months = usage.map(u => ({ u, cost: calcCost(u, tiers) }));
      return { type: 'a', months, total: months.reduce((s, m) => s + m.cost, 0) };
    }
    if (scenarioId === 'b') {
      const pooled = usage.reduce((s, u) => s + u, 0);
      return { type: 'b', pooled, total: calcCost(pooled, tiers) };
    }
    // scenario c
    let cumUsage = 0, cumCost = 0;
    const months = usage.map((u) => {
      const prevUsage = cumUsage;
      const prevCost  = cumCost;
      cumUsage += u;
      cumCost   = calcCost(cumUsage, tiers);
      return { u, cumUsage, prevUsage, prevCost, cumCost, invoice: cumCost - prevCost };
    });
    return { type: 'c', months, total: months.reduce((s, m) => s + m.invoice, 0) };
  }, [scenarioId, usage, tiers]);

  function toggleExpand(key) {
    setExpanded(prev => ({ ...prev, [key]: !prev[key] }));
  }

  function setUsageAt(i, val) {
    const next = [...usage];
    next[i] = Math.max(0, Number(val) || 0);
    setUsage(next);
  }

  function setTierRate(i, val) {
    setTiers(prev => prev.map((t, j) => j === i ? { ...t, rate: Math.max(0, Number(val) || 0) } : t));
  }

  function setTierUpTo(i, val) {
    setTiers(prev => prev.map((t, j) => j === i ? { ...t, upTo: Math.max(1, Number(val) || 1) } : t));
  }

  return (
    <div className="ac-root">

      {/* ── Scenario selector ── */}
      <div className="ac-scenarios">
        {SCENARIOS.map(s => (
          <button
            key={s.id}
            className={`ac-scenario${scenarioId === s.id ? ' ac-scenario--active' : ''}`}
            onClick={() => { setScenarioId(s.id); setExpanded({}); }}
          >
            <span className="ac-scenario-billing">Billing: <strong>{s.billing}</strong></span>
            <span className="ac-scenario-window">Window: <strong>{s.window}</strong></span>
          </button>
        ))}
      </div>
      <p className="ac-scenario-desc">{scenario.desc}</p>

      {/* ── Body: inputs + result ── */}
      <div className="ac-body">

        {/* Inputs */}
        <div className="ac-inputs">
          <div className="ac-input-group">
            <div className="ac-input-group-label">Monthly usage</div>
            {usage.map((u, i) => (
              <div key={i} className="ac-input-row">
                <label className="ac-input-month">{MONTH_NAMES[i]}</label>
                <input
                  type="number" min="0"
                  className="ac-input"
                  value={u}
                  onChange={e => setUsageAt(i, e.target.value)}
                />
                <span className="ac-input-suffix">units</span>
              </div>
            ))}
          </div>

          <div className="ac-input-group">
            <div className="ac-input-group-label">Graduated pricing</div>
            <div className="ac-tier-header">
              <span>Tier</span><span>Up to</span><span>Rate / unit</span>
            </div>
            {tiers.map((tier, i) => (
              <div key={i} className="ac-tier-row">
                <span className="ac-tier-num">{i + 1}</span>
                <span className="ac-tier-upto">
                  {tier.upTo === Infinity
                    ? <span className="ac-tier-inf">∞</span>
                    : <input type="number" min="1" className="ac-input ac-input--sm"
                        value={tier.upTo}
                        onChange={e => setTierUpTo(i, e.target.value)} />
                  }
                </span>
                <div className="ac-input-row ac-input-row--inline">
                  <span className="ac-input-prefix">₹</span>
                  <input type="number" min="0" step="0.5" className="ac-input ac-input--sm"
                    value={tier.rate}
                    onChange={e => setTierRate(i, e.target.value)} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Result */}
        <div className="ac-result">

          {/* Scenario A */}
          {result.type === 'a' && <>
            {result.months.map((m, i) => (
              <div key={i} className="ac-block">
                <div className="ac-block-header" onClick={() => toggleExpand(`m${i}`)}>
                  <span className="ac-block-label">{MONTH_NAMES[i]}</span>
                  <span className="ac-block-usage">{m.u} units</span>
                  <span className="ac-block-cost">{fmt(m.cost)}</span>
                  <span className="ac-block-toggle">{expanded[`m${i}`] ? '−' : '+'}</span>
                </div>
                {expanded[`m${i}`] && <TierBreakdown units={m.u} tiers={tiers} />}
              </div>
            ))}
            <div className="ac-invoice">
              <span className="ac-invoice-label">Q1 Invoice</span>
              <span className="ac-invoice-formula">
                {result.months.map((m, i) => <React.Fragment key={i}>{i > 0 && ' + '}{fmt(m.cost)}</React.Fragment>)}
              </span>
              <span className="ac-invoice-total">{fmt(result.total)}</span>
            </div>
          </>}

          {/* Scenario B */}
          {result.type === 'b' && <>
            <div className="ac-block">
              <div className="ac-block-header">
                <span className="ac-block-label">Pool usage</span>
                <span className="ac-block-usage">{usage.join(' + ')} = {result.pooled} units</span>
              </div>
            </div>
            <div className="ac-block">
              <div className="ac-block-header" onClick={() => toggleExpand('pool')}>
                <span className="ac-block-label">Pricing run</span>
                <span className="ac-block-usage">{result.pooled} units</span>
                <span className="ac-block-cost">{fmt(result.total)}</span>
                <span className="ac-block-toggle">{expanded['pool'] ? '−' : '+'}</span>
              </div>
              {expanded['pool'] && <TierBreakdown units={result.pooled} tiers={tiers} />}
            </div>
            <div className="ac-invoice">
              <span className="ac-invoice-label">Q1 Invoice</span>
              <span className="ac-invoice-formula">cost({result.pooled})</span>
              <span className="ac-invoice-total">{fmt(result.total)}</span>
            </div>
          </>}

          {/* Scenario C */}
          {result.type === 'c' && <>
            {result.months.map((m, i) => (
              <div key={i} className="ac-block">
                <div className="ac-block-header" onClick={() => toggleExpand(`m${i}`)}>
                  <span className="ac-block-label">{MONTH_NAMES[i]} invoice</span>
                  <span className="ac-block-usage">running total: {m.cumUsage} units</span>
                  <span className="ac-block-cost">{fmt(m.invoice)}</span>
                  <span className="ac-block-toggle">{expanded[`m${i}`] ? '−' : '+'}</span>
                </div>
                {expanded[`m${i}`] && (
                  <div className="ac-delta">
                    <div className="ac-delta-row">
                      <span className="ac-delta-label">cost({m.cumUsage})</span>
                      <span className="ac-delta-val">{fmt(m.cumCost)}</span>
                    </div>
                    {i > 0 && <div className="ac-delta-row ac-delta-row--sub">
                      <span className="ac-delta-label">− cost({m.prevUsage}) already billed</span>
                      <span className="ac-delta-val">− {fmt(m.prevCost)}</span>
                    </div>}
                    <TierBreakdown units={m.cumUsage} tiers={tiers} />
                  </div>
                )}
              </div>
            ))}
            <div className="ac-invoice">
              <span className="ac-invoice-label">Total billed</span>
              <span className="ac-invoice-formula">
                {result.months.map((m, i) => <React.Fragment key={i}>{i > 0 && ' + '}{fmt(m.invoice)}</React.Fragment>)}
              </span>
              <span className="ac-invoice-total">{fmt(result.total)}</span>
            </div>
          </>}

          <p className="ac-hint">Click any row to see the tier breakdown.</p>
        </div>

      </div>
    </div>
  );
}
